import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useCustomers } from '../../../hooks/useCustomers';
import { useAccounts, filterAccountsByType } from '../../../hooks/useAccounts';
import { getCurrentCompanyId } from '../../../utils/auth';
import { useAuth } from '../../../context/AuthContext';
import { formatCurrencyAs } from '../../../utils/currency';
import { calculateDueDate, formatPaymentTerms } from '../../../utils/paymentTerms';

const CreateInvoiceModal = ({ isOpen, onClose, onSubmit, invoice = null, isEditMode = false }) => {
  // Default payment terms with banking details
  const defaultPaymentTerms = `
Bank name: KCB Bank Kenya Ltd
Branch: KCB Thika
Swift code: KCBLKENX
Branch clearing code: 01108
A/C Name: Esque Kenya Limited
A/C No: 1273050967`;

  const { companyData } = useAuth();
  const companyCurrency = companyData?.currency || '';

  const [formData, setFormData] = useState({
    customer: '',
    isNewCustomer: false,
    customerName: '',
    customerEmail: '',
    documentType: 'Invoice',
    status: 'Draft',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    lineItems: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    vatRate: 16,
    notes: defaultPaymentTerms,
    terms: '',
    revenueAccountId: '',
    currency: companyCurrency || '',
    exchangeRate: 1
  });

  const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  // Get real customer data from API
  const companyId = getCurrentCompanyId();
  const { data: customers = [], isLoading: customersLoading } = useCustomers(companyId);
  
  // Get accounts data and filter for revenue accounts
  const { data: accounts = [], isLoading: accountsLoading } = useAccounts(companyId);
  const revenueAccounts = filterAccountsByType(accounts, ['Revenue', 'Income']);

  // Load invoice data when in edit mode
  React.useEffect(() => {
    if (isEditMode && invoice) {
      const rawLineItems = Array.isArray(invoice?.lineItems)
        ? invoice.lineItems
        : Array.isArray(invoice?.Items)
          ? invoice.Items
          : [];
      const mappedLineItems = rawLineItems.length > 0
        ? rawLineItems.map(item => ({
            description: item?.description || item?.Description || '',
            quantity: toNumber(item?.quantity ?? item?.Quantity, 1),
            rate: toNumber(item?.rate ?? item?.unitPrice ?? item?.UnitPrice),
            amount: toNumber(item?.amount ?? item?.Amount)
          }))
        : [{ description: '', quantity: 1, rate: 0, amount: 0 }];
      const invoiceTaxAmount = toNumber(invoice?.taxAmount ?? invoice?.TaxAmount);
      const explicitSubtotal = invoice?.subtotal ?? invoice?.Subtotal;
      const computedSubtotal = explicitSubtotal != null
        ? toNumber(explicitSubtotal)
        : mappedLineItems.reduce((sum, item) => sum + item.amount, 0);
      const explicitVatRate = invoice?.vatRate ?? invoice?.VatRate;
      const derivedVatRate = explicitVatRate != null
        ? toNumber(explicitVatRate)
        : computedSubtotal > 0 && invoiceTaxAmount > 0
          ? Number(((invoiceTaxAmount / computedSubtotal) * 100).toFixed(2))
          : 0;

      setFormData({
        customer: invoice?.customerId || invoice?.CustomerId || '',
        isNewCustomer: !(invoice?.customerId || invoice?.CustomerId),
        customerName: invoice?.customerName || invoice?.CustomerName || '',
        customerEmail: invoice?.customerEmail || invoice?.CustomerEmail || '',
        documentType: invoice?.documentType || invoice?.DocumentType || 'Invoice',
        status: invoice?.status || invoice?.Status || 'Draft',
        issueDate: invoice?.issueDate || invoice?.IssueDate ? new Date(invoice?.issueDate || invoice?.IssueDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        dueDate: invoice?.dueDate || invoice?.DueDate ? new Date(invoice?.dueDate || invoice?.DueDate).toISOString().split('T')[0] : '',
        lineItems: mappedLineItems,
        vatRate: derivedVatRate,
        notes: invoice?.notes || invoice?.Notes || defaultPaymentTerms,
        terms: invoice?.terms || invoice?.Terms || '',
        revenueAccountId: invoice?.revenueAccountId || invoice?.RevenueAccountId || '',
        currency: invoice?.currency || invoice?.Currency || companyCurrency || '',
        exchangeRate: toNumber(invoice?.exchangeRate ?? invoice?.ExchangeRate, 1)
      });
    } else if (!isEditMode) {
      // Reset form when creating new invoice
      setFormData({
        customer: '',
        isNewCustomer: false,
        customerName: '',
        customerEmail: '',
        documentType: 'Invoice',
        status: 'Draft',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        lineItems: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
        vatRate: 16,
        notes: defaultPaymentTerms,
        terms: '',
        revenueAccountId: '',
        currency: companyCurrency || '',
        exchangeRate: 1
      });
    }
  }, [isEditMode, invoice, isOpen, companyCurrency]);

  // Convert customers to select options
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: customer.customerName || customer.companyName,
    email: customer.email,
    contactPerson: customer.contactPerson
  })).concat({ value: '__new_customer__', label: 'New customer (not registered)' });

  // Convert revenue accounts to select options
  const revenueAccountOptions = revenueAccounts.map(account => ({
    value: account.id,
    label: `${account.code} - ${account.name}`,
    type: account.type
  }));

  // Handle customer selection and auto-calculate due date
  const handleCustomerChange = (customerId) => {
    if (customerId === '__new_customer__') {
      setFormData({
        ...formData,
        customer: '',
        isNewCustomer: true,
        dueDate: calculateDueDate(formData.issueDate)
      });
      return;
    }

    const selectedCustomer = customers.find(c => c.id === customerId);
    const newDueDate = calculateDueDate(formData.issueDate, selectedCustomer?.paymentTerms);
    
    setFormData({
      ...formData,
      customer: customerId,
      isNewCustomer: false,
      customerName: selectedCustomer?.customerName || selectedCustomer?.companyName || '',
      customerEmail: selectedCustomer?.email || '',
      dueDate: newDueDate
    });
  };

  // Handle issue date change and recalculate due date
  const handleIssueDateChange = (issueDate) => {
    const selectedCustomer = formData.isNewCustomer ? null : customers.find(c => c.id === formData.customer);
    const newDueDate = calculateDueDate(issueDate, selectedCustomer?.paymentTerms);
    
    setFormData({
      ...formData,
      issueDate,
      dueDate: newDueDate
    });
  };

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [...formData?.lineItems, { description: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const handleRemoveLineItem = (index) => {
    const newLineItems = formData?.lineItems?.filter((_, i) => i !== index);
    setFormData({ ...formData, lineItems: newLineItems });
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData?.lineItems];
    newLineItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newLineItems[index].amount = newLineItems?.[index]?.quantity * newLineItems?.[index]?.rate;
    }
    
    setFormData({ ...formData, lineItems: newLineItems });
  };

  const calculateSubtotal = () => {
    return formData?.lineItems?.reduce((sum, item) => sum + item?.amount, 0);
  };

  const calculateVAT = () => {
    return calculateSubtotal() * (formData.vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  const formatAmount = (amount) => {
    return formatCurrencyAs(amount, formData.currency);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Basic validation
    if (!formData.isNewCustomer && !formData.customer) {
      alert('Please select a customer');
      return;
    }

    if (formData.isNewCustomer && (!formData.customerName.trim() || !formData.customerEmail.trim())) {
      alert('Please enter the new customer name and email');
      return;
    }
    
    if (formData.documentType === 'Invoice' && !formData.revenueAccountId) {
      alert('Please select a revenue account');
      return;
    }
    
    if (!formData.dueDate) {
      alert('Please select a due date');
      return;
    }
    
    if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.rate <= 0)) {
      alert('Please fill in all line item details');
      return;
    }
    
    onSubmit({
      ...formData,
      customer: formData.isNewCustomer ? '' : formData.customer,
      subtotal: calculateSubtotal(),
      vat: calculateVAT(),
      total: calculateTotal()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {isEditMode ? `Edit ${formData.documentType}` : `Create New ${formData.documentType === 'Proforma' ? 'Proforma Invoice' : formData.documentType}`}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Customer"
                options={customerOptions}
                value={formData.isNewCustomer ? '__new_customer__' : formData?.customer}
                onChange={handleCustomerChange}
                required
                searchable
                loading={customersLoading}
                placeholder={customersLoading ? "Loading customers..." : "Select a customer"}
              />

              {!isEditMode ? (
                <Select
                  label="Document Type"
                  options={[
                    { value: 'Invoice', label: 'Invoice' },
                    { value: 'Proforma', label: 'Proforma Invoice' },
                    { value: 'Quote', label: 'Quote' }
                  ]}
                  value={formData.documentType}
                  onChange={(documentType) => setFormData({ ...formData, documentType })}
                  required
                />
              ) : (
                <Input label="Document Type" value={formData.documentType} disabled />
              )}

              {formData.isNewCustomer && (
                <>
                  <Input
                    label="Customer Name"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    required
                    placeholder="Enter customer or company name"
                  />
                  <Input
                    label="Customer Email"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    required
                    placeholder="Enter customer email"
                  />
                </>
              )}

              <Input
                label="Issue Date"
                type="date"
                value={formData?.issueDate}
                onChange={(e) => handleIssueDateChange(e?.target?.value)}
                required
              />

              <div>
                <Input
                  label={formData.documentType === 'Quote' ? 'Valid Until' : 'Due Date'}
                  type="date"
                  value={formData?.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e?.target?.value })}
                  required
                />
                {formData.customer && !formData.isNewCustomer && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto-calculated based on customer's payment terms: {formatPaymentTerms(customers.find(c => c.id === formData.customer)?.paymentTerms)}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.documentType === 'Invoice' && (
                <Select
                  label="Revenue Account"
                  options={revenueAccountOptions}
                  value={formData?.revenueAccountId}
                  onChange={(value) => setFormData({ ...formData, revenueAccountId: value })}
                  required
                  searchable
                  loading={accountsLoading}
                  placeholder={accountsLoading ? "Loading accounts..." : "Select revenue account"}
                />
              )}
              
              <Input
                label="VAT Rate (%)"
                type="number"
                value={formData?.vatRate}
                onChange={(e) => setFormData({ ...formData, vatRate: parseFloat(e?.target?.value) || 0 })}
                min="0"
                max="100"
                step="0.01"
                required
                placeholder="16"
              />
            </div>

            {formData.documentType !== 'Invoice' && (
              <div className="rounded-lg bg-muted/30 p-3 text-sm text-muted-foreground">
                {formData.documentType === 'Quote'
                  ? 'Quotes are estimates only and will not create accounting entries.'
                  : 'Proforma invoices are preliminary documents and will not create accounting entries.'}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Invoice Currency"
                options={[
                  { value: 'KES', label: 'KES – Kenyan Shilling' },
                  { value: 'USD', label: 'USD – US Dollar' },
                  { value: 'EUR', label: 'EUR – Euro' },
                  { value: 'GBP', label: 'GBP – British Pound' },
                ]}
                value={formData?.currency}
                onChange={(value) => setFormData({
                  ...formData,
                  currency: value,
                  exchangeRate: value === 'KES' ? 1 : formData.exchangeRate
                })}
              />

              {formData.currency !== 'KES' && (
                <Input
                  label={`Exchange Rate (1 ${formData.currency} = ? KES)`}
                  type="number"
                  value={formData?.exchangeRate}
                  onChange={(e) => setFormData({ ...formData, exchangeRate: parseFloat(e?.target?.value) || 1 })}
                  min="0.0001"
                  step="0.01"
                  required
                  placeholder="e.g. 130"
                  description={`KES equivalent: ${formData.currency !== 'KES' ? formatCurrencyAs(calculateTotal() * formData.exchangeRate, 'KES') : '—'}`}
                />
              )}
            </div>

            <div className="flex items-center text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
              <span>Prices are VAT exclusive. VAT will be calculated and added to the total.</span>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-lg text-foreground">Line Items</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLineItem}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Item
                </Button>
              </div>

              <div className="space-y-3">
                {formData?.lineItems?.map((item, index) => (
                  <div key={index} className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="caption text-muted-foreground">Item {index + 1}</span>
                      {formData?.lineItems?.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveLineItem(index)}
                          iconName="Trash2"
                          className="text-error"
                        />
                      )}
                    </div>

                    <Input
                      label="Description"
                      type="text"
                      value={item?.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e?.target?.value)}
                      placeholder="Enter item description"
                      required
                    />

                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        label="Quantity"
                        type="number"
                        value={item?.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e?.target?.value) || 0)}
                        min="0"
                        step="0.01"
                        required
                      />

                      <Input
                        label="Rate (VAT Excl.)"
                        type="number"
                        value={item?.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', parseFloat(e?.target?.value) || 0)}
                        min="0"
                        step="0.01"
                        required
                      />

                      <div>
                        <label className="block caption text-muted-foreground mb-2">Amount (VAT Excl.)</label>
                        <div className="h-10 px-3 rounded-md bg-muted border border-input flex items-center">
                          <span className="data-text font-semibold text-foreground">
                            {formatAmount(item?.amount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-muted-foreground">Subtotal (VAT Excl.)</span>
                <span className="data-text font-semibold text-foreground">
                  {formatAmount(calculateSubtotal())}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-muted-foreground">VAT ({formData.vatRate}%)</span>
                <span className="data-text font-semibold text-foreground">
                  {formatAmount(calculateVAT())}
                </span>
              </div>
              <div className="pt-2 border-t border-border flex items-center justify-between">
                <span className="font-heading font-semibold text-base text-foreground">Total (VAT Incl.)</span>
                <span className="data-text font-bold text-xl text-primary">
                  {formatAmount(calculateTotal())}
                </span>
              </div>
              {formData.currency !== 'KES' && formData.exchangeRate > 0 && (
                <div className="flex items-center justify-between pt-1">
                  <span className="font-body text-xs text-muted-foreground">KES Equivalent (@ {formData.exchangeRate})</span>
                  <span className="data-text text-sm font-semibold text-muted-foreground">
                    {formatCurrencyAs(calculateTotal() * formData.exchangeRate, 'KES')}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block caption text-muted-foreground mb-2">Payment Terms</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  placeholder="Enter payment terms and banking details..."
                  rows="8"
                  className="w-full px-3 py-2 rounded-md bg-background border border-input text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Banking details are pre-filled but can be edited as needed
                </p>
              </div>

              <div>
                <label className="block caption text-muted-foreground mb-2">Terms & Conditions</label>
                <textarea
                  value={formData?.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e?.target?.value })}
                  placeholder="Add terms and conditions..."
                  rows="8"
                  className="w-full px-3 py-2 rounded-md bg-background border border-input text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={false}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
            disabled={false}
          >
            {isEditMode ? `Update ${formData.documentType}` : `Create ${formData.documentType === 'Proforma' ? 'Proforma Invoice' : formData.documentType}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;